/**
 * AiAssistantPanel — плавающая панель ИИ-помощника поверх контента.
 *
 * Панель не сдвигает страницу: это карточка справа со своим синим градиентом.
 * Внутри — сцена с анимацией, последнее действие (запрос -> карточки ответа)
 * и поле ввода с непрерывной диктовкой.
 *
 * Показывается только последнее действие: лента в БД хранится целиком, но в
 * виджете не накапливается, чтобы панель оставалась аккуратной.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    IoCloseOutline,
    IoMicOutline,
    IoPersonOutline,
    IoSendOutline,
    IoSparklesOutline,
    IoStopOutline,
    IoRefreshOutline,
} from "react-icons/io5";
import { RiRobot2Line } from "react-icons/ri";

import AiAssistantBlocks from "./AiAssistantBlocks";
import { AiAssistantStepsLive } from "./AiAssistantSteps";
import {
    STAGE_THINKING,
    STAGE_TRANSCRIBING,
    useAiAssistant,
} from "./AiAssistantContext";
import "./aiAssistant.css";

const MAX_INPUT_LENGTH = 2000;

export default function AiAssistantPanel() {
    const { t } = useTranslation("aiAssistant");
    const {
        open,
        setOpen,
        feed,
        stage,
        lastError,
        progress,
        patient,
        setPatientId,
        sendText,
        confirmAction,
        resetSession,
        navigateRequest,
        takeNavigate,
        refresh,
        listening,
        level,
        audioError,
        liveText,
        startListening,
        stopListening,
    } = useAiAssistant();

    const navigate = useNavigate();
    const location = useLocation();

    const [text, setText] = useState("");
    const [openedNote, setOpenedNote] = useState("");
    const [confirmedToken, setConfirmedToken] = useState("");
    const [confirmBusy, setConfirmBusy] = useState(false);

    const busyRef = useRef(false);
    const inputRef = useRef(null);
    const appliedNavRef = useRef(0);

    const busy = stage === STAGE_THINKING || stage === STAGE_TRANSCRIBING;
    busyRef.current = busy;

    /** Последнее действие: ответ агента или ещё не отвеченный запрос. */
    const last = feed.length ? feed[feed.length - 1] : null;
    const pendingQuery = last && last.role !== "assistant" ? last.content || "" : "";

    /** Новая карточка в ленте — снимаем отметку прошлого подтверждения. */
    useEffect(() => {
        setConfirmedToken("");
        setConfirmBusy(false);
    }, [last?.id]);

    /** Текущий пациент из адреса страницы — уходит в промпт как контекст. */
    useEffect(() => {
        const match = /\/patient\/(\d+)/.exec(location.pathname || "");
        setPatientId(match ? Number(match[1]) : 0);
    }, [location.pathname, setPatientId]);

    /** Подгрузка ленты при открытии — история живёт в БД. */
    useEffect(() => {
        if (open) {
            refresh?.();
            window.setTimeout(() => inputRef.current?.focus(), 260);
        }
    }, [open, refresh]);

    /**
     * Агент перевёл пользователя на страницу — панель остаётся открытой.
     *
     * Запрос навигации сбрасывается сразу после перехода (takeNavigate):
     * иначе он живёт в контексте и при любом перемонтировании/изменении
     * navigate снова возвращал бы пользователя на ту же страницу, из-за чего
     * ссылки и меню казались “заблокированными”.
     */
    useEffect(() => {
        if (!navigateRequest?.path) return undefined;
        const stamp = navigateRequest.at || 0;
        if (appliedNavRef.current === stamp) return undefined;
        appliedNavRef.current = stamp;

        navigate(navigateRequest.path);
        setOpenedNote(navigateRequest.title || navigateRequest.path);
        takeNavigate?.();

        const timer = window.setTimeout(() => setOpenedNote(""), 4000);
        return () => window.clearTimeout(timer);
    }, [navigateRequest, navigate, takeNavigate]);

    /** Закрытие по Esc. */
    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setOpen(false);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open, setOpen]);

    const submitText = useCallback(async () => {
        const value = text.trim();
        if (!value || busyRef.current) return;
        if (listening) {
            await stopListening();
        }
        setText("");
        await sendText(value);
    }, [text, listening, sendText, stopListening]);

    const onInputKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            submitText();
        }
    };

    const openPatient = useCallback(
        (id) => {
            if (id > 0) navigate(`/patient/${id}/overview`);
        },
        [navigate],
    );
    const openPlan = useCallback(
        (patientId, planId) => {
            if (patientId > 0 && planId > 0) navigate(`/patient/${patientId}/plan/${planId}`);
        },
        [navigate],
    );
    const openDoc = useCallback(
        (patientId, path, docId) => {
            if (patientId > 0 && docId > 0 && path) {
                navigate(`/patient/${patientId}/profile/${path}/${docId}`);
            }
        },
        [navigate],
    );

    /**
     * Кнопка в карточке подтверждения. Действие выполняет сервер по токену —
     * модель не участвует, поэтому повторных «подтвердите ещё раз» больше нет.
     */
    const handleConfirm = useCallback(
        (agreed, block) => {
            const token = String(block?.token || "");
            if (confirmBusy) return;
            setConfirmBusy(true);
            if (agreed) setConfirmedToken(token);
            Promise.resolve(confirmAction?.(block, Boolean(agreed))).finally(() => {
                setConfirmBusy(false);
            });
        },
        [confirmAction, confirmBusy],
    );

    const hint = useMemo(() => {
        if (audioError) return audioError;
        if (stage === STAGE_THINKING) return t("hint.thinking");
        if (stage === STAGE_TRANSCRIBING) return t("hint.transcribing");
        if (listening) return t("hint.listening");
        return last ? t("hint.ready") : t("hint.start");
    }, [audioError, stage, listening, last, t]);

    /** Состояние сцены: пока микрофон включён, показываем «слушаю». */
    const heroStage = stage === STAGE_THINKING ? STAGE_THINKING : listening ? "listening" : stage;

    const bars = useMemo(() => {
        const amplitude = 0.18 + (listening ? level : 0) * 0.82;
        return Array.from({ length: 9 }, (_, index) => {
            const wave = Math.sin((index + 1) * 0.95);
            const height = (0.3 + amplitude * 0.7) * (18 + wave * 11);
            return Math.max(4, Math.min(48, Math.round(height)));
        });
    }, [listening, level]);

    return (
        <aside
            className={`aia-panel${open ? " is-open" : ""}${last ? " has-feed" : ""}`}
            aria-hidden={!open}>
            <header className="aia-panel__head">
                <div className="aia-panel__brand">
                    <span className="aia-panel__logo">
                        <RiRobot2Line size={18} />
                    </span>
                    <span className="aia-panel__title">{t("title")}</span>
                </div>
                <div className="aia-panel__tools">
                    <button
                        type="button"
                        className="aia-icon-btn"
                        onClick={resetSession}
                        title={t("actions.reset")}
                        aria-label={t("actions.reset")}>
                        <IoRefreshOutline size={17} />
                    </button>
                    <button
                        type="button"
                        className="aia-icon-btn"
                        onClick={() => setOpen(false)}
                        title={t("actions.close")}
                        aria-label={t("actions.close")}>
                        <IoCloseOutline size={19} />
                    </button>
                </div>
            </header>

            {/* Действующий пациент: с ним агент работает по умолчанию.
                Видно всегда, поэтому «он опять взял старого» больше не
                незаметно. Клик — открыть его карточку. */}
            {patient?.id ? (
                <button
                    type="button"
                    className="aia-patient"
                    onClick={() => openPatient(patient.id)}
                    title={t("patient.open", {
                        name: patient.fio || t("patient.unknown", { id: patient.id }),
                    })}>
                    <IoPersonOutline size={14} />
                    <span className="aia-patient__label">{t("patient.current")}</span>
                    <span className="aia-patient__name">
                        {patient.fio || t("patient.unknown", { id: patient.id })}
                    </span>
                    <span className="aia-patient__id">#{patient.id}</span>
                </button>
            ) : null}

            <div className={`aia-hero aia-hero--${heroStage}`}>
                <div className="aia-hero__glow" aria-hidden="true" />
                <div className="aia-hero__ring" aria-hidden="true" />
                <div className="aia-hero__waves" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                </div>
                <div className="aia-hero__pulse" aria-hidden="true">
                    <RiRobot2Line size={last ? 18 : 26} />
                </div>
                <div className="aia-hero__hint">{hint}</div>
                <div className="aia-hero__bars" aria-hidden="true">
                    {bars.map((height, index) => (
                        <span
                            key={index}
                            style={{ height: `${height}px` }}
                            className={listening ? "is-live" : ""}
                        />
                    ))}
                </div>
                {listening ? (
                    <div className="aia-hero__live">
                        <span className="aia-hero__live-dot" />
                        {liveText ? liveText.slice(-170) : t("hint.listeningIdle")}
                    </div>
                ) : null}
                {openedNote ? (
                    <div className="aia-hero__opened">
                        <IoSparklesOutline size={13} /> {t("hint.opened", { title: openedNote })}
                    </div>
                ) : null}
            </div>

            <div className="aia-stage">
                {!last && !liveText && !busy ? (
                    <div className="aia-empty">
                        <div className="aia-empty__title">{t("empty.title")}</div>
                        <ul className="aia-empty__list">
                            {(Array.isArray(t("empty.examples", { returnObjects: true }))
                                ? t("empty.examples", { returnObjects: true })
                                : []
                            ).map((line) => (
                                <li key={line}>{line}</li>
                            ))}
                        </ul>
                    </div>
                ) : null}

                {/* Запрос: последний отправленный или распознанная речь. */}
                {pendingQuery ? (
                    <div className="aia-query">{pendingQuery}</div>
                ) : !last && liveText ? (
                    <div className="aia-query">{liveText}</div>
                ) : null}

                {/* Результат последнего действия.
                    Пока агент думает над новым запросом, старое скрыто: иначе
                    на экране висит прошлый план или карточка, и кажется, что
                    агент отвечает ими. */}
                {last?.role === "assistant" && !busy ? (
                    <>
                        {last.content ? <div className="aia-answer">{last.content}</div> : null}
                        <AiAssistantBlocks
                            blocks={last.blocks}
                            onOpenPatient={openPatient}
                            onOpenPlan={openPlan}
                            onOpenDoc={openDoc}
                            onConfirm={handleConfirm}
                            confirmBusy={confirmBusy}
                            confirmedToken={confirmedToken}
                        />
                    </>
                ) : null}

                {busy ? (
                    <>
                        {/* key по id запроса: новый запрос — новый список действий,
                            шаги прошлого запроса не «переезжают» в текущий. */}
                        <AiAssistantStepsLive key={progress?.id || "probe"} progress={progress} />
                        {!progress?.steps?.length ? (
                            <div className="aia-thinking">
                                <span className="aia-thinking__dot" />
                                <span className="aia-thinking__dot" />
                                <span className="aia-thinking__dot" />
                                <span className="aia-thinking__text">{hint}</span>
                            </div>
                        ) : null}
                    </>
                ) : null}

                {lastError ? <div className="aia-error">{lastError}</div> : null}
            </div>

            <div className="aia-composer">
                <div className={`aia-input${listening ? " is-recording" : ""}`}>
                    <textarea
                        ref={inputRef}
                        className="aia-input__field"
                        value={text}
                        maxLength={MAX_INPUT_LENGTH}
                        rows={1}
                        placeholder={
                            listening ? t("input.placeholderRecording") : t("input.placeholder")
                        }
                        onChange={(event) => setText(event.target.value)}
                        onKeyDown={onInputKeyDown}
                        disabled={listening}
                    />
                    <button
                        type="button"
                        className={`aia-mic${listening ? " is-recording" : ""}`}
                        onClick={listening ? stopListening : startListening}
                        disabled={busy && !listening}
                        title={listening ? t("actions.stopRecording") : t("actions.startRecording")}
                        aria-label={
                            listening ? t("actions.stopRecording") : t("actions.startRecording")
                        }>
                        {listening ? <IoStopOutline size={18} /> : <IoMicOutline size={18} />}
                    </button>
                    <button
                        type="button"
                        className="aia-send"
                        onClick={submitText}
                        disabled={busy || listening || !text.trim()}
                        title={t("actions.send")}
                        aria-label={t("actions.send")}>
                        <IoSendOutline size={17} />
                    </button>
                </div>
                <div className="aia-hintline">{t("input.hint")}</div>
            </div>
        </aside>
    );
}
